import React, { Component } from 'react';
import { 
  ExclamationTriangleIcon, 
  ArrowPathIcon, 
  HomeIcon,
  WifiIcon,
  XCircleIcon 
} from '@heroicons/react/24/outline';

// Error fallback component with hooks
function ErrorFallback({ error, resetError, errorInfo }) {
  const [retryCount, setRetryCount] = React.useState(0);

  const handleRefresh = () => {
    setRetryCount(prev => prev + 1);
    resetError();
  };

  const handleGoHome = () => {
    resetError();
    // Use window.location instead of navigate since we might be outside Router
    window.location.href = '/';
  };

  const isDevelopment = process.env.NODE_ENV === 'development';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-lg shadow-xl p-6 md:p-8">
          {/* Header */}
          <div className="flex items-center justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-red-100 rounded-full animate-ping opacity-75"></div>
              <ExclamationTriangleIcon className="relative w-16 h-16 text-red-600" />
            </div>
          </div>

          {/* Title and Description */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Oops! Something went wrong
            </h1>
            <p className="text-gray-600">
              The dashboard encountered an unexpected error. Don't worry, your data is safe.
            </p>
          </div>

          {/* Error Details (Dev Mode) */}
          {isDevelopment && (
            <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
              <h3 className="text-sm font-semibold text-red-800 mb-2">
                Error Details (Development Mode)
              </h3>
              <div className="text-xs font-mono text-red-700 space-y-1">
                <p>{error?.message || 'Unknown error'}</p>
                {error?.stack && (
                  <details className="mt-2">
                    <summary className="cursor-pointer hover:underline">Stack trace</summary>
                    <pre className="mt-2 whitespace-pre-wrap overflow-x-auto">
                      {error.stack}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          )}

          {/* Connection Status */}
          <div className="mb-6 p-3 bg-gray-50 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <WifiIcon className="w-5 h-5 text-gray-600" />
              <span className="text-sm text-gray-700">Connection Status:</span>
            </div>
            <span className={`text-sm font-medium ${
              navigator.onLine ? 'text-green-600' : 'text-red-600'
            }`}>
              {navigator.onLine ? 'Online' : 'Offline'}
            </span>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={handleRefresh}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <ArrowPathIcon className="w-5 h-5" />
              Try Again
              {retryCount > 0 && (
                <span className="text-sm opacity-75">
                  (Attempt {retryCount + 1})
                </span>
              )}
            </button>

            <button
              onClick={handleGoHome}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              <HomeIcon className="w-5 h-5" />
              Go to Dashboard Home
            </button>

            <button
              onClick={() => window.location.reload()}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 text-gray-600 hover:text-gray-800 transition-colors text-sm"
            >
              <ArrowPathIcon className="w-4 h-4" />
              Hard Refresh (Reload Page)
            </button>
          </div>

          {/* Additional Help */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>If this problem persists, please contact support.</p>
            {retryCount > 2 && (
              <p className="mt-2 text-orange-600">
                Multiple retry attempts detected. Consider refreshing the entire page or checking your network connection.
              </p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 grid grid-cols-2 gap-4">
          <a
            href="/logout"
            className="text-center text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Sign Out
          </a>
          <button
            onClick={() => {
              localStorage.clear();
              sessionStorage.clear();
              window.location.reload();
            }}
            className="text-center text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Clear Cache & Reload
          </button>
        </div>
      </div>
    </div>
  );
}

// Main Error Boundary Class Component
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Update state with error details
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // You could also log to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
    
    // Auto-refresh after 3 consecutive errors (optional)
    if (this.state.errorCount >= 3) {
      setTimeout(() => {
        console.warn('Too many errors, performing hard refresh...');
        window.location.reload();
      }, 5000);
    }
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          resetError={this.resetError}
        />
      );
    }

    return this.props.children;
  }
}

// Hook for imperative error handling
export const useErrorHandler = () => {
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return setError;
};

// Component-specific error boundary with custom fallback
export const ComponentErrorBoundary = ({ children, fallback, onError }) => {
  return (
    <ErrorBoundary>
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;